import { Injectable } from '@angular/core';
import {AngularFirestore, DocumentReference} from '@angular/fire/firestore';
import {SessionService} from './session.service';
import firebase from 'firebase';
import firestore = firebase.firestore;
import {COLLECTION_NAME} from '../../shared/constants';
import {MasterService} from './master.service';
import {MessageService} from '../../core/service/message.service';
import {CommonService} from './common.service';

type Crud = 'create' | 'read' | 'update' | 'delete';

export interface PushText {
  info: string,
  err: string
}

@Injectable({
  providedIn: 'root'
})
export class FirestoreMasterService {
  defaultPushText: PushText = { info: '追加しました', err: '追加に失敗しました' };
  constructor(private db: AngularFirestore,
              private sessionService: SessionService,
              private messageService: MessageService,
              private masterService: MasterService,
              private commonService: CommonService,) {}

  /**
   *
   * @param collectionName
   * @param isDeleted
   */
  getMaster(collectionName = '', isDeleted = false) {
    if (!this.sessionService.session.personalSetting) { return; }
    console.log('start getMaster:', collectionName);
    return this.getMasterRefWithDeleted(collectionName, isDeleted)?.valueChanges({idField: 'id'});
  }

  /**
   * マスタDoc保存処理
   * @param collectionName
   * @param data
   * @param docId 指定されていない場合は新規にIdを振り出す
   * @param crud 更新区分 「read」が入ることは無い
   */
  saveMasterDoc(collectionName: string, data: any, docId = '', crud: Crud = 'create') {
    const batch = this.db.firestore.batch();
    const domain = this.sessionService.session.personalSetting.customerId;
    const ref: DocumentReference = this.db.firestore.collection(COLLECTION_NAME.CUSTOMERS).doc(domain).collection('masters').doc('koushinKaisuuSum');
    batch.update(ref, {[`map.${collectionName}`]: firebase.firestore.FieldValue.increment(1)});
    let masterRef;
    if (docId) {
      masterRef = this.getMasterRef(collectionName).doc(docId).ref;
    } else {
      masterRef = this.getMasterRef(collectionName).doc().ref;
    }
    if (crud === 'create') {
      batch.set(masterRef, data);
    } else if (crud === 'update') {
      batch.update(masterRef, data);
    } else if (crud === 'delete') {
      batch.delete(masterRef);
    }
    return batch.commit();
  }

  /**
   * @param collectionName
   * @param data
   * @param docId 事前にdocIdを生成する場合、「''」 をセットする想定
   * @param preCreaterDocId 事前にdocIdを生成するかフラグ
   * @param pushText 通知内容を設定。値を渡さない場合は「追加しました（に失敗しました）」が通知される。
   *                 何も通知してほしくない場合は、pushTextに空のオブジェクトを渡すかpushText.infoもしくはerrに空の文字列を渡せば良い
   */
  addMaster(collectionName: string, data: any, docId = '', preCreaterDocId = false, pushText: PushText = this.defaultPushText) {
    console.log('start addMaster');
    // 事前にdocIdを生成する場合、docId=''の予定だが、念のため !docId チェック
    if (preCreaterDocId && !docId) {
      docId = this.db.createId();
      data.id = docId;
    }
    return this.saveMasterDoc(collectionName, data, docId).then(() => {
      if (pushText?.info) {
        this.messageService.pushInfo(pushText.info);
      }
      console.log('document successfully registered!');
    }).catch(error => {
      if (pushText?.err) {
        this.messageService.pushError(pushText.err);
      }
      console.log(error);
    });
  }

  /**
   *
   * @param collectionName
   * @param data
   */
  addMasterWithId(collectionName: string, data: any) {
    if (!this.sessionService.session.user) { return; }
    // isDeletedがfalseかつ同じIDが既に存在しているかチェック
    // @ts-ignore
    const idExists = this.masterService[collectionName].filter(item => {
      return item.id === data.id && !item.isDeleted;
    });
    if (idExists.length) {
      // 同じIDが既に存在していたらエラー通知
      this.messageService.pushError('同じ鶏種が登録されています');
      console.log('the keishuId already exists');
    } else {
      // 同じIDでなければ新規登録
      console.log('start addMaster');
      this.saveMasterDoc(collectionName, data, data.id).then(() => {
        this.messageService.pushInfo('追加しました');
        console.log('document successfully registered!');
      }).catch(error => {
        this.messageService.pushError('追加に失敗しました');
        console.log(error);
      });
    }
  }

  /**
   * @param collectionName
   * @param data
   * @param pushText 通知内容を設定。値を渡さない場合は「更新しました（に失敗しました）」が通知される。
   *                 何も通知してほしくない場合は、pushTextに空のオブジェクトを渡すかpushText.infoもしくはerrに空の文字列を渡せば良い
   */
  updateMaster(collectionName: string, data: any, pushText: PushText = this.defaultPushText) {
    if (!this.sessionService.session.user) { return; }
    console.log('start updateMaster');
    this.saveMasterDoc(collectionName, data, data.id, 'update').then(() => {
      if (pushText?.info) {
        this.messageService.pushInfo(pushText.info);
      }
      console.log('document successfully updated!');
    }).catch(error => {
      if (pushText?.err) {
        this.messageService.pushError(pushText.err);
      }
      console.log(error);
    });
  }

  /**
   *
   * @param collectionName
   * @param data
   */
  deleteMasterPermanently(collectionName: string, data: any) {
    if (!this.sessionService.session.user) { return; }
    console.log('start deleteMasterPermanently');
    this.saveMasterDoc(collectionName, data, data.id, 'delete').then(() => {
      this.messageService.pushInfo('削除しました');
      console.log('document successfully deleted!');
    }).catch(error => {
      this.messageService.pushError('削除に失敗しました');
      console.log(error);
    });
  }

  /**
   *
   * @param collectionName
   * @param data
   */
  deleteMaster(collectionName: string, data: any) {
    if (!this.sessionService.session.user) { return; }
    console.log('start deleteMaster');
    // デリートフラグをTrue
    data.isDeleted = true;
    const batch = this.db.firestore.batch();
    const domain = this.sessionService.session.personalSetting.customerId;
    const ref: DocumentReference = this.db.firestore.collection(COLLECTION_NAME.CUSTOMERS).doc(domain).collection('masters').doc('koushinKaisuuSum');
    // collectionNameが農場の場合、ぶら下がっている鶏舎マスタもデリートフラグをTrue
    if (collectionName === COLLECTION_NAME.FARMMASTER) {
      // 削除したい農場マスタのidと鶏舎マスタのfarmId一致しているデータ取得
      const keishaMasterList = this.masterService.keishaMaster.filter(keisha => {
        return keisha.farmId === data.id;
      });
      const keishaMaster = this.getMasterRef(COLLECTION_NAME.KEISHAMASTER);
      for (let i = 0; i < keishaMasterList.length; i++) {
        // 各鶏舎マスタのデリートフラグをTrueに変更、更新
        keishaMasterList[i].isDeleted = true;
        console.log(keishaMasterList[i]);
        // const a = keishaMasterList[i].id;
        // console.log(a);
        const keishaMasterRef = keishaMaster.doc(keishaMasterList[i].id).ref;
        batch.update(keishaMasterRef, keishaMasterList[i]);
      }
      // 鶏舎マスタ更新回数を更新
      batch.update(ref, {['map.COLLECTION_NAME.KEISHAMASTER']: firebase.firestore.FieldValue.increment(1)});
    }
    // マスタ更新回数を更新
    batch.update(ref, {[`map.${collectionName}`]: firebase.firestore.FieldValue.increment(1)});
    const masterRef = this.getMasterRef(collectionName).doc(data.id).ref;
    batch.update(masterRef, data);
    batch.commit().then(() => {
      this.messageService.pushInfo('削除しました');
      console.log('document successfully deleted!');
    }).catch(error => {
      this.messageService.pushError('削除に失敗しました');
      console.log(error);
    });
  }

  /**
   *
   * @param collectionName
   */
  getMasterRef(collectionName: string) {
    console.log('getMasterRef');
    const domain = this.sessionService.session.personalSetting.customerId;
    if (collectionName === COLLECTION_NAME.CUSTOMERS) {
      return this.db.collection(COLLECTION_NAME.CUSTOMERS);
    }
    return this.db.collection(COLLECTION_NAME.CUSTOMERS).doc(domain).collection(collectionName);
  }

  /**
   *
   * @param collectionName
   * @param isDeleted
   */
  getMasterRefWithDeleted(collectionName = '', isDeleted: boolean) {
    if (!this.sessionService.session.personalSetting) { return; }
    // const domain = this.sessionService.session.personalSetting.customerId;
    const domain = this.sessionService.session.personalSetting.customerId;
    if (!collectionName) {
      return this.db.collection(COLLECTION_NAME.CUSTOMERS).doc(domain);
    }
    return this.db.collection(COLLECTION_NAME.CUSTOMERS).doc(domain).collection(collectionName,
      ref => {
        let query:
          | firestore.CollectionReference
          | firestore.Query = ref;
        if (isDeleted !== false) {
          query = query.where('isDeleted', '==', isDeleted);
        }
        query = query.orderBy('name', 'asc');
        return query;
      });
  }

  // /**
  //  * 投与方法の一括更新を行う
  //  * 「左胸筋」→「筋注」
  //  * 2022-08-17実行
  //  * ワクチンマスタは手動で更新
  //  */
  // touyoHouhouIkkatsuKoushin() {
  //   console.log('touyoHouhouIkkatsuKoushin starts.');
  //   if (!confirm('更新しますか?')) { return; }
  //   const batch = this.db.firestore.batch();
  //   const promiseArray = [
  //     // this.getCollectionGroupWithTouyoHouhou(COLLECTION_NAME.VACCINEMASTER, '左胸筋', batch),
  //     this.getCollectionGroupWithTouyoHouhou('左胸筋', '筋注', batch)];
  //   Promise.all(promiseArray).then(() => {
  //     if (!confirm('更新しますか?')) { return; }
  //     batch.commit();
  //     console.log('touyoHouhouIkkatsuKoushin completed.');
  //   });
  // }
  //
  // /**
  //  * イベントDocumentの投与方法を
  //  * 指定した値に更新
  //  * @param touyoHouhou
  //  * @param henkouGoTouyohouhou
  //  * @param batch
  //  */
  // async getCollectionGroupWithTouyoHouhou(touyoHouhou: string, henkouGoTouyohouhou: string,batch: firebase.firestore.WriteBatch) {
  //   const snapshot = await this.db.firestore.collectionGroup(COLLECTION_NAME.EVENT).where('touyoHouhou', '==', touyoHouhou).get();
  //   if (snapshot.empty) { return; }
  //   console.log('touyoHouhou: ', touyoHouhou);
  //   const list = [];
  //   for (const doc of snapshot.docs) {
  //     if (!doc.exists) { continue; }
  //     const data = doc.data();
  //     data.touyoHouhou = henkouGoTouyohouhou;
  //     if (data.eventName) {
  //       data.eventName = data.eventName.replace(touyoHouhou, henkouGoTouyohouhou);
  //     }
  //     list.push(data);
  //     batch.set(doc.ref, data, {merge: true});
  //   }
  //   console.table(list);
  // }

  // /**
  //  * isDeleted 管理しているマスタで
  //  * isDeleted が未定義の場合、falseを一括でセットする。
  //  * 2022-10-03 実施
  //  */
  // async updateSpecificPropertyOfMaster() {
  //   // isDeleted 管理しているマスタ
  //   const collectionNames = [
  //     COLLECTION_NAME.FARMMASTER,
  //     COLLECTION_NAME.KEISHAMASTER,
  //     COLLECTION_NAME.SHUKKASAKIMASTER,
  //     COLLECTION_NAME.KEISHUMASTER,
  //     COLLECTION_NAME.VACCINEPROGRAMMASTER,
  //     COLLECTION_NAME.TENTOUMASTER,
  //     COLLECTION_NAME.MOKUHYOUTEMPERATUREMASTER,
  //     COLLECTION_NAME.SHIBOUKEIREPORTMASTER,
  //   ];
  //   const batch = this.db.firestore.batch();
  //   const domain = this.sessionService.session.personalSetting.customerId;
  //   const masterKoushinKaisuuRef: DocumentReference
  //     = this.db.firestore.collection(COLLECTION_NAME.CUSTOMERS).doc(domain).collection('masters').doc('koushinKaisuuSum');
  //
  //   for (let collectionName of collectionNames) {
  //     await this.batchSetIsDeletedOfMasterCollection(domain, collectionName, batch, masterKoushinKaisuuRef);
  //   }
  //
  //   if (!confirm('更新しますか?')) { return; }
  //   batch.commit().then(() => {
  //     console.log('setting isDeleted property of master is completed.');
  //   });
  // }

  // /**
  //  * isDeleted が未定義の場合、falseを一括でバッチセットする。
  //  * 更新対象がある場合、更新回数もインクリメントする。
  //  * @param domain
  //  * @param collectionName
  //  * @param batch
  //  */
  // async batchSetIsDeletedOfMasterCollection(domain: string, collectionName: string,
  //                                           batch: firebase.firestore.WriteBatch, masterKoushinKaisuuRef: DocumentReference) {
  //   const ref = this.db.firestore.collection(COLLECTION_NAME.CUSTOMERS).doc(domain).collection(collectionName);
  //   const masterSnapshots = await ref.get();
  //   if (masterSnapshots.empty) { return; }
  //   const list = [];
  //   for (const doc of masterSnapshots.docs) {
  //     if (!doc.exists) { continue; }
  //     const data = doc.data() as {isDeleted: boolean, hover?: boolean};
  //     // 既に isDeleted が定義済の場合は何もしない。
  //     if (typeof data.isDeleted !== "undefined") {
  //       continue;
  //     }
  //     data.isDeleted = false;
  //     // hover が定義されていたら削除
  //     if (typeof data.hover !== "undefined") {
  //       delete data.hover;
  //     }
  //     list.push(data);
  //     batch.set(doc.ref, data, {merge: true});
  //   }
  //   if (list.length > 0) {
  //     // マスタ更新回数を更新
  //     batch.update(masterKoushinKaisuuRef, {[`map.${collectionName}`]: firebase.firestore.FieldValue.increment(1)});
  //   }
  //   console.table(list);
  // }
}
